import { VITE_API_URL, VITE_APP_BASENAME } from '@/config';
import { t } from '@/i18n';
import { PAGE_LAYOUT_MIN_WIDTH, PUBLIC_SITE_FONT_FAMILY } from '@/utils';
import styles from '../assets/scss/styles.module.scss';
import { getBrowserInfo } from './getBrowserInfo';

export const PS_DEFAULT_SEARCH_START_YEAR = 2021;

// frontend/src/features/public-site/files/ にあるファイルが、下記 GitHub Actions で S3 にアップロードされる
// https://github.com/weldrow-com/bns/blob/dev/.github/workflows/deploy-fe-ps.yml
const FILES_BASE_URL = `${VITE_API_URL}${VITE_APP_BASENAME}/files`;

export const VIEW_PORT_WIDTH_FOR_SP = 1280;

export const BREAK_POINTS = {
  spMax: 768,
  pcMin: PAGE_LAYOUT_MIN_WIDTH,
  pcSm: 1020,
  pcMd: 1280,
} as const;

const _THEME = {
  fonts: {
    fontFamily: PUBLIC_SITE_FONT_FAMILY,
  },
  colors: {
    textPrimary: styles.colorPrimary,
    textSecondary: styles.colorSecondary,
    textTertiary: '#00000099',
    fill: styles.colorPrimary,
    fillSecondary: '#1E417B',
    bgPrimary: styles.colorBgPrimary,
    link: '#E0ECFF',
  },
  zIndexies: {
    header: 1000,
    footer: 3,
  },
} as const;

export const SITE_NAME = t('ps-site-name');

export const SITE_URL = 'https://rssystem.go.jp' as const;

export const PAGE_INFO = {
  top: {
    title: undefined,
    path: '/top',
    shouldForcePcViewport: false,
    footerType: 'main',
    createPath() {
      return this.path;
    },
  },
  project: {
    title: t('ps-page-info-projects-title'),
    path: '/project',
    shouldForcePcViewport: true,
    footerType: 'sub',
    createPath() {
      return this.path;
    },
  },
  projectDetail: {
    title: t('ps-page-info-project-title'),
    path: '/project/:id',
    shouldForcePcViewport: true,
    footerType: 'sub',
    createPath(pathname: string) {
      const [, , id] = pathname.split('/');
      return id ? this.path.replace(':id', id) : undefined;
    },
  },
  projectReport: {
    title: t('ps-page-info-project-report-title'),
    path: '/project/:id/report',
    absolutePath: '/report',
    shouldForcePcViewport: true,
    footerType: 'sub',
    createPath(pathname: string) {
      const [, , id] = pathname.split('/');
      return id ? this.path.replace(':id', id) : undefined;
    },
  },
  projectDiff: {
    title: t('ps-page-info-project-diff-title'),
    path: '/project/:id/diff',
    absolutePath: '/diff',
    shouldForcePcViewport: true,
    footerType: 'sub',
    createPath(pathname: string) {
      const [, , id] = pathname.split('/');
      return id ? this.path.replace(':id', id) : undefined;
    },
  },
  analysis: {
    title: t('ps-page-info-analysis-title'),
    path: '/analysis',
    shouldForcePcViewport: true,
    footerType: 'sub',
    createPath() {
      return this.path;
    },
  },
  sheets: {
    title: t('ps-page-info-sheets-title'),
    path: '/sheets',
    shouldForcePcViewport: false,
    footerType: 'main',
    createPath() {
      return this.path;
    },
  },
  sheetsMinistry: {
    title: t('ps-page-info-sheets-title'),
    path: '/sheets/:year',
    shouldForcePcViewport: false,
    footerType: 'main',
    createPath(pathname: string) {
      const [, , year] = pathname.split('/');
      return year ? this.path.replace(':year', year) : undefined;
    },
  },
  sheetsList: {
    title: t('ps-page-info-sheets-title'),
    path: '/sheets/:year/:ministry',
    shouldForcePcViewport: false,
    footerType: 'main',
    createPath(pathname: string) {
      const [, , year, ministry] = pathname.split('/');
      return year && ministry
        ? this.path.replace(':year', year).replace(':ministry', ministry)
        : undefined;
    },
  },
  downloadCsv: {
    title: t('ps-page-info-download-csv-title'),
    path: '/download-csv',
    shouldForcePcViewport: false,
    footerType: 'main',
    createPath() {
      return this.path;
    },
  },
  downloadCsvList: {
    title: t('ps-page-info-download-csv-title'),
    path: '/download-csv/:year',
    shouldForcePcViewport: false,
    footerType: 'main',
    createPath(year: string) {
      return this.path.replace(':year', year);
    },
  },
  notice: {
    title: t('ps-page-info-notice-title'),
    path: '/notice',
    shouldForcePcViewport: false,
    footerType: 'main',
    createPath() {
      return this.path;
    },
  },
  howTo: {
    title: t('ps-page-info-how-to-title'),
    path: `${FILES_BASE_URL}/user-guide.pdf`,
    shouldForcePcViewport: false,
    footerType: 'main',
    createPath() {
      return;
    },
  },
  contact: {
    title: t('ps-page-info-contact-title'),
    path: '/contact',
    shouldForcePcViewport: false,
    footerType: 'main',
    createPath() {
      return this.path;
    },
  },
  terms: {
    title: t('ps-page-info-terms-title'),
    path: '/terms',
    shouldForcePcViewport: false,
    footerType: 'main',
    createPath() {
      return this.path;
    },
  },
  privacyPolicy: {
    title: t('ps-page-info-privacy-policy-title'),
    path: '/privacy-policy',
    shouldForcePcViewport: false,
    footerType: 'main',
    createPath() {
      return this.path;
    },
  },
  notFound: {
    title: t('ps-page-info-404-title'),
    path: '*',
    shouldForcePcViewport: false,
    footerType: 'main',
    createPath() {
      return '/404';
    },
  },
} as const;

const _MENU_ITEMS = [
  {
    key: 'top',
    label: t('ps-menu-top'),
  },
  {
    key: 'project',
    label: t('ps-menu-project'),
  },
  {
    key: 'analysis',
    label: t('ps-menu-analysis'),
  },
  {
    key: 'howTo',
    label: t('ps-menu-how-to'),
  },
  {
    key: 'notice',
    label: t('ps-menu-notice'),
  },
  {
    key: 'sheets',
    label: t('ps-menu-sheets'),
  },
  {
    key: 'downloadCsv',
    label: t('ps-menu-download-csv'),
  },
  {
    key: 'contact',
    label: t('ps-menu-contact'),
  },
] as const;

export type MinistryKey = (typeof MINISTRY_KEYS)[number];

const MINISTRY_KEYS = [
  'cabinet-secretariat-cabinet-office',
  'fair-trade-commission',
  'national-police-agency',
  'personal-information-protection-commission',
  'casino-regulatory-commission',
  'financial-services-agency',
  'consumer-affairs-agency',
  'children-and-families-agency',
  'digital-agency',
  'reconstruction-agency',
  'ministry-of-internal-affairs-and-communications',
  'ministry-of-justice',
  'ministry-of-foreign-affairs',
  'ministry-of-finance',
  'ministry-of-education-culture-sports-science-and-technology',
  'ministry-of-health-labour-and-welfare',
  'ministry-of-agriculture-forestry-and-fisheries',
  'ministry-of-economy-trade-and-industry',
  'ministry-of-land-infrastructure-and-transport',
  'ministry-of-the-environment',
  'nuclear-regulation-authority',
  'ministry-of-defense',
] as const;

type SheetInfo = {
  specificMinistry?: 'cabinet-office' | 'cabinet-secretariat';
  fileType:
    | 'rsFormat1'
    | 'rsFormat2'
    | 'rsFormat3'
    | 'rsFormat4'
    | 'ks'
    | 'ksLocalGovernmentA'
    | 'ksLocalGovernmentB'
    | 'ksInvestment';
  fileName: string;
};

const _SHEETS_DATA: {
  year: string;
  ministries: {
    key: MinistryKey;
    name: string;
    sheetsData: {
      rs: SheetInfo[];
      ks?: SheetInfo[];
      ksLocalGovernment?: SheetInfo[];
      ksInvestment?: SheetInfo[];
    };
  }[];
}[] = [
  {
    year: '2024',
    ministries: [
      {
        key: 'cabinet-secretariat-cabinet-office',
        name: t('ps-ministry-name-cabinet-secretariat-cabinet-office'),
        sheetsData: {
          rs: [
            {
              specificMinistry: 'cabinet-secretariat',
              fileType: 'rsFormat1',
              fileName: t(
                'ps-sheet-name-2024-cabinet-secretariat-rs-project-list-format1',
              ),
            },
            {
              specificMinistry: 'cabinet-secretariat',
              fileType: 'rsFormat4',
              fileName: t(
                'ps-sheet-name-2024-cabinet-secretariat-rs-summary-format4',
              ),
            },
            {
              specificMinistry: 'cabinet-office',
              fileType: 'rsFormat1',
              fileName: t(
                'ps-sheet-name-2024-cabinet-office-rs-project-list-format1',
              ),
            },
            {
              specificMinistry: 'cabinet-office',
              fileType: 'rsFormat2',
              fileName: t(
                'ps-sheet-name-2024-cabinet-office-rs-project-list-format2',
              ),
            },
            {
              specificMinistry: 'cabinet-office',
              fileType: 'rsFormat3',
              fileName: t(
                'ps-sheet-name-2024-cabinet-office-rs-project-list-format3',
              ),
            },
            {
              specificMinistry: 'cabinet-office',
              fileType: 'rsFormat4',
              fileName: t(
                'ps-sheet-name-2024-cabinet-office-rs-summary-format4',
              ),
            },
          ],
          ks: [
            {
              specificMinistry: 'cabinet-office',
              fileType: 'ks',
              fileName: t('ps-sheet-name-2024-cabinet-office-ks'),
            },
          ],
          ksLocalGovernment: [
            {
              specificMinistry: 'cabinet-office',
              fileType: 'ksLocalGovernmentA',
              fileName: t(
                'ps-sheet-name-2024-cabinet-office-ks-fund-status-list-of-local-government-basic-information',
              ),
            },
            {
              specificMinistry: 'cabinet-office',
              fileType: 'ksLocalGovernmentB',
              fileName: t(
                'ps-sheet-name-2024-cabinet-office-ks-fund-status-list-of-local-government-income-and-expenditure',
              ),
            },
          ],
          ksInvestment: [
            {
              specificMinistry: 'cabinet-office',
              fileType: 'ksInvestment',
              fileName: t('ps-sheet-name-2024-cabinet-office-ks-investment'),
            },
          ],
        },
      },
      {
        key: 'fair-trade-commission',
        name: t('ps-ministry-name-fair-trade-commission'),
        sheetsData: {
          rs: [
            {
              fileType: 'rsFormat1',
              fileName: t(
                'ps-sheet-name-2024-fair-trade-commission-rs-project-list-format1',
              ),
            },
            {
              fileType: 'rsFormat4',
              fileName: t(
                'ps-sheet-name-2024-fair-trade-commission-rs-summary-format4',
              ),
            },
          ],
        },
      },
      {
        key: 'national-police-agency',
        name: t('ps-ministry-name-national-police-agency'),
        sheetsData: {
          rs: [
            {
              fileType: 'rsFormat1',
              fileName: t(
                'ps-sheet-name-2024-national-police-agency-rs-project-list-format1',
              ),
            },
            {
              fileType: 'rsFormat2',
              fileName: t(
                'ps-sheet-name-2024-national-police-agency-rs-project-list-format2',
              ),
            },
            {
              fileType: 'rsFormat3',
              fileName: t(
                'ps-sheet-name-2024-national-police-agency-rs-project-list-format3',
              ),
            },
            {
              fileType: 'rsFormat4',
              fileName: t(
                'ps-sheet-name-2024-national-police-agency-rs-summary-format4',
              ),
            },
          ],
        },
      },
      {
        key: 'personal-information-protection-commission',
        name: t('ps-ministry-name-personal-information-protection-commission'),
        sheetsData: {
          rs: [
            {
              fileType: 'rsFormat1',
              fileName: t(
                'ps-sheet-name-2024-personal-information-protection-commission-rs-project-list-format1',
              ),
            },
            {
              fileType: 'rsFormat4',
              fileName: t(
                'ps-sheet-name-2024-personal-information-protection-commission-rs-summary-format4',
              ),
            },
          ],
        },
      },
      {
        key: 'casino-regulatory-commission',
        name: t('ps-ministry-name-casino-regulatory-commission'),
        sheetsData: {
          rs: [
            {
              fileType: 'rsFormat1',
              fileName: t(
                'ps-sheet-name-2024-casino-regulatory-commission-rs-project-list-format1',
              ),
            },
            {
              fileType: 'rsFormat4',
              fileName: t(
                'ps-sheet-name-2024-casino-regulatory-commission-rs-summary-format4',
              ),
            },
          ],
        },
      },
      {
        key: 'financial-services-agency',
        name: t('ps-ministry-name-financial-services-agency'),
        sheetsData: {
          rs: [
            {
              fileType: 'rsFormat1',
              fileName: t(
                'ps-sheet-name-2024-financial-services-agency-rs-project-list-format1',
              ),
            },
            {
              fileType: 'rsFormat2',
              fileName: t(
                'ps-sheet-name-2024-financial-services-agency-rs-project-list-format2',
              ),
            },
            {
              fileType: 'rsFormat3',
              fileName: t(
                'ps-sheet-name-2024-financial-services-agency-rs-project-list-format3',
              ),
            },
            {
              fileType: 'rsFormat4',
              fileName: t(
                'ps-sheet-name-2024-financial-services-agency-rs-summary-format4',
              ),
            },
          ],
        },
      },
      {
        key: 'consumer-affairs-agency',
        name: t('ps-ministry-name-consumer-affairs-agency'),
        sheetsData: {
          rs: [
            {
              fileType: 'rsFormat1',
              fileName: t(
                'ps-sheet-name-2024-consumer-affairs-agency-rs-project-list-format1',
              ),
            },
            {
              fileType: 'rsFormat3',
              fileName: t(
                'ps-sheet-name-2024-consumer-affairs-agency-rs-project-list-format3',
              ),
            },
            {
              fileType: 'rsFormat4',
              fileName: t(
                'ps-sheet-name-2024-consumer-affairs-agency-rs-summary-format4',
              ),
            },
          ],
        },
      },
      {
        key: 'children-and-families-agency',
        name: t('ps-ministry-name-children-and-families-agency'),
        sheetsData: {
          rs: [
            {
              fileType: 'rsFormat1',
              fileName: t(
                'ps-sheet-name-2024-children-and-families-agency-rs-project-list-format1',
              ),
            },
            {
              fileType: 'rsFormat2',
              fileName: t(
                'ps-sheet-name-2024-children-and-families-agency-rs-project-list-format2',
              ),
            },
            {
              fileType: 'rsFormat3',
              fileName: t(
                'ps-sheet-name-2024-children-and-families-agency-rs-project-list-format3',
              ),
            },
            {
              fileType: 'rsFormat4',
              fileName: t(
                'ps-sheet-name-2024-children-and-families-agency-rs-summary-format4',
              ),
            },
          ],
          ks: [
            {
              fileType: 'ks',
              fileName: t('ps-sheet-name-2024-children-and-families-agency-ks'),
            },
          ],
          ksLocalGovernment: [
            {
              fileType: 'ksLocalGovernmentA',
              fileName: t(
                'ps-sheet-name-2024-children-and-families-agency-ks-fund-status-list-of-local-government-basic-information',
              ),
            },
            {
              fileType: 'ksLocalGovernmentB',
              fileName: t(
                'ps-sheet-name-2024-children-and-families-agency-ks-fund-status-list-of-local-government-income-and-expenditure',
              ),
            },
          ],
        },
      },
      {
        key: 'digital-agency',
        name: t('ps-ministry-name-digital-agency'),
        sheetsData: {
          rs: [
            {
              fileType: 'rsFormat1',
              fileName: t(
                'ps-sheet-name-2024-digital-agency-rs-project-list-format1',
              ),
            },
            {
              fileType: 'rsFormat2',
              fileName: t(
                'ps-sheet-name-2024-digital-agency-rs-project-list-format2',
              ),
            },
            {
              fileType: 'rsFormat3',
              fileName: t(
                'ps-sheet-name-2024-digital-agency-rs-project-list-format3',
              ),
            },
            {
              fileType: 'rsFormat4',
              fileName: t(
                'ps-sheet-name-2024-digital-agency-rs-summary-format4',
              ),
            },
          ],
        },
      },
      {
        key: 'reconstruction-agency',
        name: t('ps-ministry-name-reconstruction-agency'),
        sheetsData: {
          rs: [
            {
              fileType: 'rsFormat1',
              fileName: t(
                'ps-sheet-name-2024-reconstruction-agency-rs-project-list-format1',
              ),
            },
            {
              fileType: 'rsFormat3',
              fileName: t(
                'ps-sheet-name-2024-reconstruction-agency-rs-project-list-format3',
              ),
            },
            {
              fileType: 'rsFormat4',
              fileName: t(
                'ps-sheet-name-2024-reconstruction-agency-rs-summary-format4',
              ),
            },
          ],
          ks: [
            {
              fileType: 'ks',
              fileName: t('ps-sheet-name-2024-reconstruction-agency-ks'),
            },
          ],
          ksLocalGovernment: [
            {
              fileType: 'ksLocalGovernmentA',
              fileName: t(
                'ps-sheet-name-2024-reconstruction-agency-ks-fund-status-list-of-local-government-basic-information',
              ),
            },
            {
              fileType: 'ksLocalGovernmentB',
              fileName: t(
                'ps-sheet-name-2024-reconstruction-agency-ks-fund-status-list-of-local-government-income-and-expenditure',
              ),
            },
          ],
          ksInvestment: [
            {
              fileType: 'ksInvestment',
              fileName: t(
                'ps-sheet-name-2024-reconstruction-agency-ks-investment',
              ),
            },
          ],
        },
      },
      {
        key: 'ministry-of-internal-affairs-and-communications',
        name: t(
          'ps-ministry-name-ministry-of-internal-affairs-and-communications',
        ),
        sheetsData: {
          rs: [
            {
              fileType: 'rsFormat1',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-internal-affairs-and-communications-rs-project-list-format1',
              ),
            },
            {
              fileType: 'rsFormat2',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-internal-affairs-and-communications-rs-project-list-format2',
              ),
            },
            {
              fileType: 'rsFormat3',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-internal-affairs-and-communications-rs-project-list-format3',
              ),
            },
            {
              fileType: 'rsFormat4',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-internal-affairs-and-communications-rs-summary-format4',
              ),
            },
          ],
          ks: [
            {
              fileType: 'ks',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-internal-affairs-and-communications-ks',
              ),
            },
          ],
          ksInvestment: [
            {
              fileType: 'ksInvestment',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-internal-affairs-and-communications-ks-investment',
              ),
            },
          ],
        },
      },
      {
        key: 'ministry-of-justice',
        name: t('ps-ministry-name-ministry-of-justice'),
        sheetsData: {
          rs: [
            {
              fileType: 'rsFormat1',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-justice-rs-project-list-format1',
              ),
            },
            {
              fileType: 'rsFormat3',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-justice-rs-project-list-format3',
              ),
            },
            {
              fileType: 'rsFormat4',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-justice-rs-summary-format4',
              ),
            },
          ],
        },
      },
      {
        key: 'ministry-of-foreign-affairs',
        name: t('ps-ministry-name-ministry-of-foreign-affairs'),
        sheetsData: {
          rs: [
            {
              fileType: 'rsFormat1',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-foreign-affairs-rs-project-list-format1',
              ),
            },
            {
              fileType: 'rsFormat2',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-foreign-affairs-rs-project-list-format2',
              ),
            },
            {
              fileType: 'rsFormat3',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-foreign-affairs-rs-project-list-format3',
              ),
            },
            {
              fileType: 'rsFormat4',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-foreign-affairs-rs-summary-format4',
              ),
            },
          ],
          ks: [
            {
              fileType: 'ks',
              fileName: t('ps-sheet-name-2024-ministry-of-foreign-affairs-ks'),
            },
          ],
          ksInvestment: [
            {
              fileType: 'ksInvestment',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-foreign-affairs-ks-investment',
              ),
            },
          ],
        },
      },
      {
        key: 'ministry-of-finance',
        name: t('ps-ministry-name-ministry-of-finance'),
        sheetsData: {
          rs: [
            {
              fileType: 'rsFormat1',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-finance-rs-project-list-format1',
              ),
            },
            {
              fileType: 'rsFormat3',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-finance-rs-project-list-format3',
              ),
            },
            {
              fileType: 'rsFormat4',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-finance-rs-summary-format4',
              ),
            },
          ],
          ks: [
            {
              fileType: 'ks',
              fileName: t('ps-sheet-name-2024-ministry-of-finance-ks'),
            },
          ],
        },
      },
      {
        key: 'ministry-of-education-culture-sports-science-and-technology',
        name: t(
          'ps-ministry-name-ministry-of-education-culture-sports-science-and-technology',
        ),
        sheetsData: {
          rs: [
            {
              fileType: 'rsFormat1',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-education-culture-sports-science-and-technology-rs-project-list-format1',
              ),
            },
            {
              fileType: 'rsFormat2',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-education-culture-sports-science-and-technology-rs-project-list-format2',
              ),
            },
            {
              fileType: 'rsFormat3',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-education-culture-sports-science-and-technology-rs-project-list-format3',
              ),
            },
            {
              fileType: 'rsFormat4',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-education-culture-sports-science-and-technology-rs-summary-format4',
              ),
            },
          ],
          ks: [
            {
              fileType: 'ks',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-education-culture-sports-science-and-technology-ks',
              ),
            },
          ],
          ksLocalGovernment: [
            {
              fileType: 'ksLocalGovernmentA',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-education-culture-sports-science-and-technology-ks-fund-status-list-of-local-government-basic-information',
              ),
            },
            {
              fileType: 'ksLocalGovernmentB',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-education-culture-sports-science-and-technology-ks-fund-status-list-of-local-government-income-and-expenditure',
              ),
            },
          ],
          ksInvestment: [
            {
              fileType: 'ksInvestment',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-education-culture-sports-science-and-technology-ks-investment',
              ),
            },
          ],
        },
      },
      {
        key: 'ministry-of-health-labour-and-welfare',
        name: t('ps-ministry-name-ministry-of-health-labour-and-welfare'),
        sheetsData: {
          rs: [
            {
              fileType: 'rsFormat1',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-health-labour-and-welfare-rs-project-list-format1',
              ),
            },
            {
              fileType: 'rsFormat2',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-health-labour-and-welfare-rs-project-list-format2',
              ),
            },
            {
              fileType: 'rsFormat3',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-health-labour-and-welfare-rs-project-list-format3',
              ),
            },
            {
              fileType: 'rsFormat4',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-health-labour-and-welfare-rs-summary-format4',
              ),
            },
          ],
          ks: [
            {
              fileType: 'ks',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-health-labour-and-welfare-ks',
              ),
            },
          ],
          ksLocalGovernment: [
            {
              fileType: 'ksLocalGovernmentA',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-health-labour-and-welfare-ks-fund-status-list-of-local-government-basic-information',
              ),
            },
            {
              fileType: 'ksLocalGovernmentB',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-health-labour-and-welfare-ks-fund-status-list-of-local-government-income-and-expenditure',
              ),
            },
          ],
        },
      },
      {
        key: 'ministry-of-agriculture-forestry-and-fisheries',
        name: t(
          'ps-ministry-name-ministry-of-agriculture-forestry-and-fisheries',
        ),
        sheetsData: {
          rs: [
            {
              fileType: 'rsFormat1',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-agriculture-forestry-and-fisheries-rs-project-list-format1',
              ),
            },
            {
              fileType: 'rsFormat2',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-agriculture-forestry-and-fisheries-rs-project-list-format2',
              ),
            },
            {
              fileType: 'rsFormat3',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-agriculture-forestry-and-fisheries-rs-project-list-format3',
              ),
            },
            {
              fileType: 'rsFormat4',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-agriculture-forestry-and-fisheries-rs-summary-format4',
              ),
            },
          ],
          ks: [
            {
              fileType: 'ks',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-agriculture-forestry-and-fisheries-ks',
              ),
            },
          ],
          ksLocalGovernment: [
            {
              fileType: 'ksLocalGovernmentA',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-agriculture-forestry-and-fisheries-ks-fund-status-list-of-local-government-basic-information',
              ),
            },
            {
              fileType: 'ksLocalGovernmentB',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-agriculture-forestry-and-fisheries-ks-fund-status-list-of-local-government-income-and-expenditure',
              ),
            },
          ],
          ksInvestment: [
            {
              fileType: 'ksInvestment',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-agriculture-forestry-and-fisheries-ks-investment',
              ),
            },
          ],
        },
      },
      {
        key: 'ministry-of-economy-trade-and-industry',
        name: t('ps-ministry-name-ministry-of-economy-trade-and-industry'),
        sheetsData: {
          rs: [
            {
              fileType: 'rsFormat1',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-economy-trade-and-industry-rs-project-list-format1',
              ),
            },
            {
              fileType: 'rsFormat2',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-economy-trade-and-industry-rs-project-list-format2',
              ),
            },
            {
              fileType: 'rsFormat3',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-economy-trade-and-industry-rs-project-list-format3',
              ),
            },
            {
              fileType: 'rsFormat4',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-economy-trade-and-industry-rs-summary-format4',
              ),
            },
          ],
          ks: [
            {
              fileType: 'ks',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-economy-trade-and-industry-ks',
              ),
            },
          ],
          ksLocalGovernment: [
            {
              fileType: 'ksLocalGovernmentA',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-economy-trade-and-industry-ks-fund-status-list-of-local-government-basic-information',
              ),
            },
            {
              fileType: 'ksLocalGovernmentB',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-economy-trade-and-industry-ks-fund-status-list-of-local-government-income-and-expenditure',
              ),
            },
          ],
          ksInvestment: [
            {
              fileType: 'ksInvestment',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-economy-trade-and-industry-ks-investment',
              ),
            },
          ],
        },
      },
      {
        key: 'ministry-of-land-infrastructure-and-transport',
        name: t(
          'ps-ministry-name-ministry-of-land-infrastructure-and-transport',
        ),
        sheetsData: {
          rs: [
            {
              fileType: 'rsFormat1',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-land-infrastructure-and-transport-rs-project-list-format1',
              ),
            },
            {
              fileType: 'rsFormat2',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-land-infrastructure-and-transport-rs-project-list-format2',
              ),
            },
            {
              fileType: 'rsFormat3',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-land-infrastructure-and-transport-rs-project-list-format3',
              ),
            },
            {
              fileType: 'rsFormat4',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-land-infrastructure-and-transport-rs-summary-format4',
              ),
            },
          ],
          ks: [
            {
              fileType: 'ks',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-land-infrastructure-and-transport-ks',
              ),
            },
          ],
          ksLocalGovernment: [
            {
              fileType: 'ksLocalGovernmentA',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-land-infrastructure-and-transport-ks-fund-status-list-of-local-government-basic-information',
              ),
            },
            {
              fileType: 'ksLocalGovernmentB',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-land-infrastructure-and-transport-ks-fund-status-list-of-local-government-income-and-expenditure',
              ),
            },
          ],
          ksInvestment: [
            {
              fileType: 'ksInvestment',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-land-infrastructure-and-transport-ks-investment',
              ),
            },
          ],
        },
      },
      {
        key: 'ministry-of-the-environment',
        name: t('ps-ministry-name-ministry-of-the-environment'),
        sheetsData: {
          rs: [
            {
              fileType: 'rsFormat1',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-the-environment-rs-project-list-format1',
              ),
            },
            {
              fileType: 'rsFormat2',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-the-environment-rs-project-list-format2',
              ),
            },
            {
              fileType: 'rsFormat3',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-the-environment-rs-project-list-format3',
              ),
            },
            {
              fileType: 'rsFormat4',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-the-environment-rs-summary-format4',
              ),
            },
          ],
          ks: [
            {
              fileType: 'ks',
              fileName: t('ps-sheet-name-2024-ministry-of-the-environment-ks'),
            },
          ],
          ksLocalGovernment: [
            {
              fileType: 'ksLocalGovernmentA',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-the-environment-ks-fund-status-list-of-local-government-basic-information',
              ),
            },
            {
              fileType: 'ksLocalGovernmentB',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-the-environment-ks-fund-status-list-of-local-government-income-and-expenditure',
              ),
            },
          ],
          ksInvestment: [
            {
              fileType: 'ksInvestment',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-the-environment-ks-investment',
              ),
            },
          ],
        },
      },
      {
        key: 'nuclear-regulation-authority',
        name: t('ps-ministry-name-nuclear-regulation-authority'),
        sheetsData: {
          rs: [
            {
              fileType: 'rsFormat1',
              fileName: t(
                'ps-sheet-name-2024-nuclear-regulation-authority-rs-project-list-format1',
              ),
            },
            {
              fileType: 'rsFormat2',
              fileName: t(
                'ps-sheet-name-2024-nuclear-regulation-authority-rs-project-list-format2',
              ),
            },
            {
              fileType: 'rsFormat3',
              fileName: t(
                'ps-sheet-name-2024-nuclear-regulation-authority-rs-project-list-format3',
              ),
            },
            {
              fileType: 'rsFormat4',
              fileName: t(
                'ps-sheet-name-2024-nuclear-regulation-authority-rs-summary-format4',
              ),
            },
          ],
        },
      },
      {
        key: 'ministry-of-defense',
        name: t('ps-ministry-name-ministry-of-defense'),
        sheetsData: {
          rs: [
            {
              fileType: 'rsFormat1',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-defense-rs-project-list-format1',
              ),
            },
            {
              fileType: 'rsFormat2',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-defense-rs-project-list-format2',
              ),
            },
            {
              fileType: 'rsFormat3',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-defense-rs-project-list-format3',
              ),
            },
            {
              fileType: 'rsFormat4',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-defense-rs-summary-format4',
              ),
            },
          ],
          ks: [
            {
              fileType: 'ks',
              fileName: t('ps-sheet-name-2024-ministry-of-defense-ks'),
            },
          ],
          ksLocalGovernment: [
            {
              fileType: 'ksLocalGovernmentA',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-defense-ks-fund-status-list-of-local-government-basic-information',
              ),
            },
            {
              fileType: 'ksLocalGovernmentB',
              fileName: t(
                'ps-sheet-name-2024-ministry-of-defense-ks-fund-status-list-of-local-government-income-and-expenditure',
              ),
            },
          ],
        },
      },
    ],
  },
];

type CsvDataGroupName =
  | 'basicInformation'
  | 'budgetAndExecution'
  | 'wayToEffect'
  | 'inspectionEvaluation'
  | 'payee'
  | 'remark';

const _CSV_DATA: {
  year: string;
  contents: {
    startYear: string;
    criteriaDate: string;
  };
  data: {
    [k in CsvDataGroupName]: {
      name: string;
      fileName: string;
    }[];
  };
}[] = [
  {
    year: '2024',
    contents: {
      startYear: '2021',
      criteriaDate: '2025年2月14日',
    },
    data: {
      basicInformation: [
        {
          name: t(
            'ps-csv-name-2024-rs-basic-information-organization-information',
          ),
          fileName: t(
            'ps-csv-file-name-2024-rs-basic-information-organization-information',
          ),
        },
        {
          name: t('ps-csv-name-2024-rs-basic-information-project-summary'),
          fileName: t(
            'ps-csv-file-name-2024-rs-basic-information-project-summary',
          ),
        },
        {
          name: t(
            'ps-csv-name-2024-rs-basic-information-policy-measure-laws-and-regulations',
          ),
          fileName: t(
            'ps-csv-file-name-2024-rs-basic-information-policy-measure-laws-and-regulations',
          ),
        },
        {
          name: t('ps-csv-name-2024-rs-basic-information-subsidy-rate'),
          fileName: t(
            'ps-csv-file-name-2024-rs-basic-information-subsidy-rate',
          ),
        },
        {
          name: t('ps-csv-name-2024-rs-basic-information-related-projects'),
          fileName: t(
            'ps-csv-file-name-2024-rs-basic-information-related-projects',
          ),
        },
      ],
      budgetAndExecution: [
        {
          name: t('ps-csv-name-2024-rs-budget-and-execution-summary'),
          fileName: t('ps-csv-file-name-2024-rs-budget-and-execution-summary'),
        },
        {
          name: t('ps-csv-name-2024-rs-budget-and-execution-detail'),
          fileName: t('ps-csv-file-name-2024-rs-budget-and-execution-detail'),
        },
      ],
      wayToEffect: [
        {
          name: t('ps-csv-name-2024-rs-wey-to-effect-purpose-result'),
          fileName: t('ps-csv-file-name-2024-rs-wey-to-effect-purpose-result'),
        },
        {
          name: t('ps-csv-name-2024-rs-wey-to-effect-purpose-connection'),
          fileName: t(
            'ps-csv-file-name-2024-rs-wey-to-effect-purpose-connection',
          ),
        },
      ],
      inspectionEvaluation: [
        {
          name: t('ps-csv-name-2024-rs-inspection-evaluation'),
          fileName: t('ps-csv-file-name-2024-rs-inspection-evaluation'),
        },
      ],
      payee: [
        {
          name: t('ps-csv-name-2024-rs-payee-payment-information'),
          fileName: t('ps-csv-file-name-2024-rs-payee-payment-information'),
        },
        {
          name: t('ps-csv-name-2024-rs-payee-payment-block-connection'),
          fileName: t(
            'ps-csv-file-name-2024-rs-payee-payment-block-connection',
          ),
        },
        {
          name: t('ps-csv-name-2024-rs-payee-payment-amount-breakdown'),
          fileName: t(
            'ps-csv-file-name-2024-rs-payee-payment-amount-breakdown',
          ),
        },
        {
          name: t('ps-csv-name-2024-rs-payee-contract'),
          fileName: t('ps-csv-file-name-2024-rs-payee-contract'),
        },
      ],
      remark: [
        {
          name: t('ps-csv-name-2024-rs-remark'),
          fileName: t('ps-csv-file-name-2024-rs-remark'),
        },
      ],
    },
  },
];

const _BROWSER_INFO = getBrowserInfo();
